import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { animate } from 'framer-motion';
import { DESIGN_WIDTH, DESIGN_HEIGHT } from '../BroadcastFrame.js';

/**
 * Spotlight — a full-frame darkening overlay with a bright circular "iris" that
 * can move + resize to focus a target rect (e.g. a PlayerBust during a reveal).
 *
 * The iris is a radial gradient positioned/sized by three CSS vars written
 * straight to the node (--sx, --sy = center in design px; --sr = radius in px),
 * so animating it never triggers a React re-render. The reveal color is
 * --spot-core / --spot-cone.
 *
 * Imperative API (via ref):
 *   snap(target)  — fast 180ms slam to the target (a beat hit)
 *   ease(target)  — slow ceremonial reveal to the target
 *   clear()       — open the iris wide (no focus)
 * A target is { x, y, r } in the 1920×1080 design space. Reduced-motion applies
 * every change instantly (opacity/position swap, no tween).
 *
 * Mount inside a BroadcastFrame (it fills its positioned parent, absolute inset 0).
 */

export interface SpotTarget {
  /** Center x in design-space px. */
  x: number;
  /** Center y in design-space px. */
  y: number;
  /** Iris radius in design-space px. */
  r: number;
}

export interface SpotlightHandle {
  snap: (target: SpotTarget) => void;
  ease: (target: SpotTarget) => void;
  clear: () => void;
}

export interface SpotlightProps {
  /** Overlay darkness 0..1 outside the iris. Default 0.72. */
  dark?: number;
  className?: string;
  style?: React.CSSProperties;
}

/** Wide-open default: centered, radius large enough to cover the frame. */
const OPEN: SpotTarget = {
  x: DESIGN_WIDTH / 2,
  y: DESIGN_HEIGHT / 2,
  r: Math.hypot(DESIGN_WIDTH, DESIGN_HEIGHT),
};

function prefersReduced(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export const Spotlight = forwardRef<SpotlightHandle, SpotlightProps>(function Spotlight(
  { dark = 0.72, className, style },
  ref,
) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const stopRef = useRef<() => void>(() => {});
  // Current values, so a tween starts from where the iris actually is.
  const cur = useRef<SpotTarget>({ ...OPEN });

  const write = (t: SpotTarget) => {
    const el = nodeRef.current;
    if (!el) return;
    el.style.setProperty('--sx', `${t.x}px`);
    el.style.setProperty('--sy', `${t.y}px`);
    el.style.setProperty('--sr', `${t.r}px`);
    cur.current = { ...t };
  };

  const run = (target: SpotTarget, durationS: number) => {
    stopRef.current();
    if (prefersReduced() || durationS <= 0) {
      write(target);
      return;
    }
    const from = { ...cur.current };
    const controls = animate(0, 1, {
      duration: durationS,
      ease: [0.7, 0, 0.16, 1], // --ease-dramatic
      onUpdate: (p) =>
        write({
          x: from.x + (target.x - from.x) * p,
          y: from.y + (target.y - from.y) * p,
          r: from.r + (target.r - from.r) * p,
        }),
    });
    stopRef.current = () => controls.stop();
  };

  useImperativeHandle(
    ref,
    () => ({
      snap: (target) => run(target, 0.18),
      ease: (target) => run(target, 1.2),
      clear: () => run(OPEN, 0.6),
    }),
    [],
  );

  // Initial CSS var values (open) via inline style; animations overwrite them.
  const initialVars = useMemo(
    () =>
      ({
        '--sx': `${OPEN.x}px`,
        '--sy': `${OPEN.y}px`,
        '--sr': `${OPEN.r}px`,
      }) as React.CSSProperties,
    [],
  );

  return (
    <div
      ref={nodeRef}
      aria-hidden
      className={className}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        ...initialVars,
        // Dark everywhere, transparent inside the iris; a thin warm cone at the edge.
        background: `radial-gradient(circle var(--sr) at var(--sx) var(--sy),
          transparent 0%,
          var(--spot-cone, rgba(247,227,176,0.35)) 82%,
          rgba(0,0,0,${dark}) 100%)`,
        ...style,
      }}
    />
  );
});
